import { Box, Typography } from '@mui/material';
import { useChildTokens } from './useChildTokens';
import { formatTime, remainingFraction, TimerViewProps, TIMER_SURFACE } from './timerView';

/**
 * Cirkeln som fylls — samma återstående tid som ringen, men utan hål i mitten.
 *
 * Skillnaden mot ringen är avsiktligt bara massan: en solid yta som krymper är
 * kraftigare och syns på längre håll, medan ringen är lättare och lämnar mitten åt
 * siffrorna. Skillnaden mot tårtbiten är riktningen — här krymper CIRKELN inifrån
 * kanten (radien), där tårtbiten sveper runt tavlan.
 *
 * Siffrorna ligger ovanpå ytan och kan stängas av; etiketten står kvar oavsett.
 * Ren renderare: tiden kommer som prop, ingen egen setInterval (se timerView.ts).
 */
const TimerDisc = ({
  time,
  totalSeconds,
  label,
  color,
  onToggle,
  showDigits = true,
}: TimerViewProps) => {
  const tokens = useChildTokens();
  const remaining = remainingFraction(time, totalSeconds);
  // Ytan (inte radien) är det ögat läser som "hur mycket är kvar", så radien följer
  // roten ur andelen: halva tiden kvar ska se ut som halva cirkeln, inte som 70 %.
  const size = TIMER_SURFACE * Math.sqrt(remaining);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2.5 }}>
      <Box
        component="button"
        type="button"
        data-testid="timer-disc"
        onClick={onToggle}
        aria-label={label}
        sx={{
          width: TIMER_SURFACE,
          height: TIMER_SURFACE,
          borderRadius: '50%',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          p: 0,
          bgcolor: tokens.border,
          border: 'none',
          fontFamily: 'inherit',
          overflow: 'hidden',
          cursor: onToggle ? 'pointer' : 'default',
        }}
      >
        <Box
          data-testid="timer-disc-fill"
          aria-hidden
          sx={{
            position: 'absolute',
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: color,
            transition: 'width 1s linear, height 1s linear',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
          }}
        >
          {showDigits && (
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 900,
                color: tokens.text,
                // Ytan under siffrorna växlar färg när cirkeln krymper förbi dem, så
                // siffrorna får en egen bakgrundsplatta i stället för att tappa kontrast.
                bgcolor: tokens.card,
                borderRadius: '10px',
                px: 1,
              }}
            >
              {formatTime(time)}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: tokens.text,
              bgcolor: tokens.card,
              borderRadius: '8px',
              px: 0.75,
              mt: 0.5,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TimerDisc;
